import React, { useEffect, useRef, useState } from "react";
import { Box, FormControl, Select, MenuItem } from "@mui/material";
import {
  PlayActiveLightIcon,
  PauseActiveLightIcon,
  CancelIcon,
} from "../../../../assets/icon";
import { SelectChangeEvent } from "@mui/material/Select";
import useChatStore from "../../../../stores/chatStore";
import { useBrowserDetection } from "../../../../hooks/useBrowserDetection";
import { fetchAuthenticatedFileBlob } from "../../../../utils/authenticatedFileUrl";

interface MinimalAudioPlayerProps {
  audio: string;
  handleClose: () => void;
  isInAudioPlayer?: boolean;
  isPlaying?: boolean;
  playbackRate?: number;
  onPlayPause?: () => void;
  onSpeedChange?: (
    event: SelectChangeEvent<number>,
    child: React.ReactNode
  ) => void;
}

export const MinimalAudioPlayer = ({
  audio,
  handleClose,
  isInAudioPlayer = false,
  isPlaying = false,
  playbackRate = 1,
  onPlayPause,
  onSpeedChange,
}: MinimalAudioPlayerProps) => {
  const { clearMessageAudio, setActiveAudioPlayer, setActiveAudioPlayerPause } =
    useChatStore();

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const prevAudioRef = useRef<string | null>(null);

  const [localIsPlaying, setLocalIsPlaying] = useState(false);
  const [localPlaybackRate, setLocalPlaybackRate] = useState<number>(1);
  const { isSafari } = useBrowserDetection();

  useEffect(() => {
    if (prevAudioRef.current === audio && audioRef.current) return;
    prevAudioRef.current = audio;

    if (isInAudioPlayer) {
      setLocalIsPlaying(isPlaying);
      setLocalPlaybackRate(playbackRate);
      return;
    }

    // For Safari, save the current state of the audio before reloading

    let wasPlaying = false;
    let currentTime = 0;

    if (isSafari && audioRef.current) {
      wasPlaying = !audioRef.current.paused;
      currentTime = audioRef.current.currentTime;
    }

    async function fetchAudio() {
      try {
        const blob = await fetchAuthenticatedFileBlob(`${audio}?no_cache=${Date.now()}`);
        const objectUrl = URL.createObjectURL(blob);

        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }
        blobUrlRef.current = objectUrl;

        if (audioRef.current) {
          audioRef.current.pause();
        }

        const audioElement = new Audio(objectUrl);
        audioElement.playbackRate = localPlaybackRate;
        audioRef.current = audioElement;

        audioElement.addEventListener("ended", handleEnded);

        if (isSafari && wasPlaying) {
          audioElement.currentTime = currentTime;
          await audioElement.play();
          setLocalIsPlaying(true);
        } else {
          await audioElement.play();
          setLocalIsPlaying(true);
        }

        setActiveAudioPlayer("minimal");
        setActiveAudioPlayerPause(() => () => {
          if (audioRef.current) {
            audioRef.current.pause();
            setLocalIsPlaying(false);
          }
        });
      } catch (error) {
        console.error("Error fetching audio:", error);
      }
    }

    fetchAudio();

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener("ended", handleEnded);
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [audio, setActiveAudioPlayer, setActiveAudioPlayerPause, isInAudioPlayer]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = localPlaybackRate;
    }
  }, [localPlaybackRate]);

  useEffect(() => {
    if (isInAudioPlayer) {
      setLocalIsPlaying(isPlaying);
    }
  }, [isPlaying, isInAudioPlayer]);

  useEffect(() => {
    if (isInAudioPlayer) {
      setLocalPlaybackRate(playbackRate);
    }
  }, [playbackRate, isInAudioPlayer]);

  const handleTogglePlay = () => {
    if (isInAudioPlayer && onPlayPause) {
      onPlayPause();
      return;
    }

    if (!audioRef.current) return;
    if (localIsPlaying) {
      audioRef.current.pause();
      setLocalIsPlaying(false);
    } else {
      audioRef.current
        .play()
        .then(() => setLocalIsPlaying(true))
        .catch((error) => console.error("Error playing audio:", error));
    }
  };

  const handleSpeedChange = (event: SelectChangeEvent<number>) => {
    const newSpeed = Number(event.target.value);

    if (isInAudioPlayer && onSpeedChange) {
      onSpeedChange(event, null);
      return;
    }
    setLocalPlaybackRate(newSpeed);
  };

  const handleCancel = () => {
    if (isInAudioPlayer) {
      handleClose();
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeEventListener("ended", handleEnded);
      setLocalIsPlaying(false);
      audioRef.current = null;
    }

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    clearMessageAudio();
    setActiveAudioPlayer(null);
    setActiveAudioPlayerPause(null);
    handleClose();
  };

  const handleEnded = () => {
    handleCancel();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        backgroundColor: "rgba(255, 255, 255, 0.8)",
        borderRadius: "12px",
        padding: { xs: "10px", sm: "10px", md: "10px" },
        margin: { xs: "29px 10px", sm: "3px", md: "5px" },
      }}
    >
      <Box
        onClick={handleTogglePlay}
        sx={{ background: "#fff", borderRadius: "25px", cursor: "pointer" }}
      >
        {localIsPlaying ? (
          <PauseActiveLightIcon style={{ width: 32, height: 32 }} />
        ) : (
          <PlayActiveLightIcon style={{ width: 32, height: 32 }} />
        )}
      </Box>

      <FormControl variant="outlined" size="small">
        <Select
          value={localPlaybackRate}
          onChange={handleSpeedChange}
          sx={{
            width: "65px",
            fontSize: "0.7rem",
            borderRadius: "12px",
            background: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#06B6D4" },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#06B6D4",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#06B6D4",
            },
            "& .MuiSvgIcon-root": { color: "#06B6D4" },
            color: "#06B6D4",
          }}
        >
          <MenuItem sx={{ fontSize: "14px" }} value={0.5}>
            0.5x
          </MenuItem>
          <MenuItem sx={{ fontSize: "14px" }} value={1}>
            1x
          </MenuItem>
          <MenuItem sx={{ fontSize: "14px" }} value={1.5}>
            1.5x
          </MenuItem>
          <MenuItem sx={{ fontSize: "14px" }} value={2}>
            2x
          </MenuItem>
        </Select>
      </FormControl>

      <Box onClick={handleCancel} sx={{ cursor: "pointer" }}>
        <CancelIcon />
      </Box>
    </Box>
  );
};

export default MinimalAudioPlayer;
